import {
  Account,
  BaseTransaction,
  Response,
  TransactionWarning,
  ValidatorsProps,
  apiAbortController,
} from '@ui/src';
import api from '../../../../api/api';
import {
  CustomerProps,
  CustomerScore,
  CustomerScoreQueuedProps,
  Balance,
  CustomerRanges,
  CofProps,
  NatureOfControlEnum,
  NewCofProps,
  CofStatus,
  OngoingMonitring,
} from '../../../customers/types';
import {
  CorporateOverviewGeneral,
  SignatureType,
  UserAccountProps,
} from '../../types';

export interface CreateCorporateCustomerValidatorResponse {
  data: {
    createClientOfficialSchema: ValidatorsProps;
    updateClientOfficialSchema: ValidatorsProps;
  };
}

export const corporateCustomersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCorporateUsers: build.query<Response<CustomerProps>, void>({
      query: (query) => ({
        url: `/v1/clients-organizations${query}&type=CORPORATE`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualUsersList: build.query<Response<CustomerProps>, string>({
      query: (query) => ({
        url: `/v1/clients-organizations${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCreateCorporateCustomerValidator: build.query<
      CreateCorporateCustomerValidatorResponse,
      void
    >({
      query: () => ({
        url: '/v1/clients-officials/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateUser: build.query<{ data: CustomerProps }, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateCOF: build.query<Response<CofProps>, string>({
      query: (id) => ({
        url: `/v1/clients-officials?organizationId=${id}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateCOF: build.mutation<
      {
        data: CofProps;
      },
      {
        id: string;
        body: {
          position: string;
          percentage?: string;
          natureOfControl?: NatureOfControlEnum;
          companyOfficialType: string;
          signatureType: SignatureType;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-officials/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addCorporateCOF: build.mutation<
      {
        data: CofProps;
      },
      NewCofProps
    >({
      query: (body) => ({
        url: '/v1/clients-officials',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCofStatus: build.mutation<
      {
        data: CofProps;
      },
      {
        id: string;
        body: {
          status: CofStatus;
          rejectionReason?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-officials/${data.id}/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporatelUserScore: build.query<{ data: CustomerScore }, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/current-score`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateAmlUpdatingItem: build.query<
      { data: CustomerScoreQueuedProps },
      { orgId: string; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/clients-scores-queue-items/${data.id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateLastCalculatingScore: build.query<
      Response<CustomerScore>,
      string
    >({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/scores`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateAccountBalance: build.query<Response<Balance>, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCorporateUserScore: build.mutation<
      {
        data: CustomerScoreQueuedProps;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/clients-scores-queue-items`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateInfo: build.mutation<
      {
        data: CustomerProps;
      },
      { body: Partial<CorporateOverviewGeneral>; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCorporateAccount: build.mutation<
      {
        data: UserAccountProps;
      },
      {
        organizationId: string;
        body: { description: string };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.organizationId}/account`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateRanges: build.mutation<
      { data: CustomerProps },
      { id: string; body: CustomerRanges }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/ranges`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    getCorporateTransactionsWithWarnings: build.query<
      Response<BaseTransaction>,
      { id: string; query: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/clients-organizations/corporates/${id}/transactions-with-warnings${query.replace(
          'take=20',
          'take=10',
        )}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    blockCorporateCredit: build.mutation<
      { data: Account },
      { id: string; accountId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/block-credit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    unblockCorporateCredit: build.mutation<
      { data: Account },
      { id: string; accountId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/unblock-credit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    blockCorporateDebit: build.mutation<
      { data: Account },
      { id: string; accountId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/block-debit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    unblockCorporateDebit: build.mutation<
      { data: Account },
      { id: string; accountId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/unblock-debit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    closeCorporateAccount: build.mutation<
      { data: Account },
      { id: string; accountId: string; body: { comment: string } }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/close`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    openCorporateAccount: build.mutation<
      { data: Account },
      { id: string; accountId: string; body: { comment: string } }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/open`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportCorporateStatements: build.query<
      string,
      { id: string; format: string; account: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/statements-items?format=${data.format}&account=${data.account}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateOngoingMonitoring: build.query<
      Response<OngoingMonitring>,
      { id: string; cursor?: string; take?: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/warnings-groups?take=${
          data.take || '10'
        }${data.cursor ? `&cursor=${data.cursor}` : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateOngoingMonitoringItem: build.query<
      { data: OngoingMonitring },
      { orgId: string; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/warnings-groups/${data.id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    applyCorporateOngoingWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        orgId: string;
        id: string;
        warningId: string;
        body: {
          action: string;
          comment: string;
          employeeId: string;
          matchStatus?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/warnings-groups/${data.id}/warnings/${data.warningId}/action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchApplyCorporateOngoingWarningAction: build.mutation<
      { data: any },
      {
        orgId: string;
        body: {
          warningGroupId: string;
          action: string;
          comment: string;
          employeeId: string;
          matchStatus?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/warnings-groups/warnings/batch-action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateMainChannel: build.query<Response<{ id: string }>, string>({
      query: (id) => ({
        url: `/v1/channels?organizationId=${id}&channelType=MAIN`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateVisibility: build.mutation<
      { data: Account & { visibleToClient: boolean } },
      { id: string; body: { visibleToClient: boolean; id: string } }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateAccountLogs: build.query<{ data: UserAccountProps }, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetCorporateWarningGroupAction: build.mutation<
      { data: TransactionWarning },
      {
        orgId: string;
        id: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/warnings-groups/${data.id}/warnings/${data.warningId}/reset`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateWarningGroupWithPagination: build.query<
      Response<TransactionWarning>,
      { id: string; warningGroupId: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/warnings-groups/${data.warningGroupId}/warnings?cursor=${data.cursor}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateCustomerClosingBalances: build.query<
      { data: Account },
      { id: string; closingBalancesAt?: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}${
          data.closingBalancesAt
            ? `?closingBalancesAt=${data.closingBalancesAt}`
            : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateOngoingWarning: build.query<
      { data: TransactionWarning },
      {
        orgId: string;
        id: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/warnings-groups/${data.id}/warnings/${data.warningId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCorporateManualRiskScore: build.mutation<
      { data: CustomerScore },
      { id: string; totalRiskScore: number }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/scores`,
        method: 'POST',
        body: {
          totalRiskScore: data.totalRiskScore,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateAccountClosingBalances: build.query<
      { data: Balance[] },
      string
    >({
      query: (query) => ({
        url: `/v1/balances/closing-balances${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetCorporateUsersQuery,
  useLazyGetCorporateUsersQuery,
  useGetCreateCorporateCustomerValidatorQuery,
  useLazyGetCorporateUserQuery,
  useLazyGetCorporatelUserScoreQuery,
  useCreateCorporateUserScoreMutation,
  useLazyGetCorporateAmlUpdatingItemQuery,
  useLazyGetCorporateLastCalculatingScoreQuery,
  useLazyGetCorporateAccountBalanceQuery,
  useCreateCorporateAccountMutation,
  useUpdateCorporateInfoMutation,
  useUpdateCorporateRangesMutation,
  useLazyGetCorporateTransactionsWithWarningsQuery,
  useBlockCorporateCreditMutation,
  useBlockCorporateDebitMutation,
  useCloseCorporateAccountMutation,
  useUnblockCorporateCreditMutation,
  useUnblockCorporateDebitMutation,
  useOpenCorporateAccountMutation,
  useLazyExportCorporateStatementsQuery,
  useLazyGetCorporateCOFQuery,
  useUpdateCorporateCOFMutation,
  useAddCorporateCOFMutation,
  useLazyGetIndividualUsersListQuery,
  useUpdateCofStatusMutation,
  useLazyGetCorporateOngoingMonitoringQuery,
  useLazyGetCorporateOngoingMonitoringItemQuery,
  useApplyCorporateOngoingWarningActionMutation,
  useBatchApplyCorporateOngoingWarningActionMutation,
  useLazyGetCorporateMainChannelQuery,
  useUpdateCorporateVisibilityMutation,
  useLazyGetCorporateAccountLogsQuery,
  useResetCorporateWarningGroupActionMutation,
  useLazyGetCorporateWarningGroupWithPaginationQuery,
  useLazyGetCorporateCustomerClosingBalancesQuery,
  useLazyGetCorporateOngoingWarningQuery,
  useCreateCorporateManualRiskScoreMutation,
  useLazyGetCorporateAccountClosingBalancesQuery,
} = corporateCustomersApi;

export const {
  endpoints: {
    getCorporateUsers,
    getCreateCorporateCustomerValidator,
    getCorporateUser,
    getCorporatelUserScore,
    createCorporateUserScore,
    getCorporateAmlUpdatingItem,
    getCorporateLastCalculatingScore,
    getCorporateAccountBalance,
    createCorporateAccount,
    updateCorporateInfo,
    updateCorporateRanges,
    getCorporateTransactionsWithWarnings,
    blockCorporateCredit,
    blockCorporateDebit,
    unblockCorporateCredit,
    unblockCorporateDebit,
    closeCorporateAccount,
    openCorporateAccount,
    exportCorporateStatements,
    getCorporateCOF,
    updateCorporateCOF,
    addCorporateCOF,
    getIndividualUsersList,
    updateCofStatus,
    getCorporateOngoingMonitoring,
    getCorporateOngoingMonitoringItem,
    applyCorporateOngoingWarningAction,
    batchApplyCorporateOngoingWarningAction,
    getCorporateMainChannel,
    updateCorporateVisibility,
    getCorporateAccountLogs,
    resetCorporateWarningGroupAction,
    getCorporateWarningGroupWithPagination,
    getCorporateCustomerClosingBalances,
    getCorporateOngoingWarning,
    createCorporateManualRiskScore,
    getCorporateAccountClosingBalances,
  },
} = corporateCustomersApi;
